/**
 * Externalized user-facing strings.
 *
 * WHY: no hard-coded copy in components/views. A single message catalog is the
 * seam for translation — the Arabic catalog lands at P8 behind the same shape.
 */
export interface Messages {
  /** Fully-qualified name — drives `document.title`, never the header. */
  readonly appTitle: string;
  /**
   * The header wordmark: the SERVICE name, without the "SquadLab · " OS prefix.
   *
   * WHY the two differ (CC-35): the OS-level Command Center chip now sits
   * immediately to the wordmark's left (ADR 0110 §2), so the header already
   * says which operating system this is. Repeating it in 20px Orbitron cost
   * ~180px of a header that was already at 93% of a 1440px viewport, and tipped
   * the whole bar onto a second row between 1360 and 1555. The tab title still
   * carries the fully-qualified name, where width is free.
   */
  readonly appWordmark: string;
  readonly badgeSimulation: string;
  readonly badgeSimulationLabel: string;
  readonly loading: string;
  readonly errorGeneric: string;
  readonly empty: string;
  /** Attention (home) view. */
  readonly attentionTitle: string;
  readonly attentionIntro: string;
  readonly attentionGates: string;
  readonly attentionExpiries: string;
  readonly attentionLeave: string;
  readonly attentionEmpty: string;
  /** Shell navigation + venture switcher. */
  readonly navAttention: string;
  readonly navPeople: string;
  readonly navCases: string;
  /** Visible label on the OS-level Command Center return link (COMMAND-4). */
  readonly navCommandCenter: string;
  /** Its accessible name — states the action *and* the destination. */
  readonly navCommandCenterLabel: string;
  readonly ventureSwitcherLabel: string;
  /** Cases list + detail. */
  readonly casesTitle: string;
  readonly casesIntro: string;
  readonly casesEmpty: string;
  readonly casesError: string;
  readonly startOnboarding: string;
  readonly startOnboardingFor: string;
  readonly caseNotFound: string;
  readonly backToCases: string;
  readonly caseStatusLabel: Readonly<Record<'open' | 'awaiting-gate' | 'completed' | 'rejected' | 'cancelled', string>>;
  readonly gateApprove: string;
  readonly gateRedo: string;
  readonly gateReject: string;
  readonly gateHeading: string;
  readonly verdictAllowed: string;
  readonly verdictBlocked: string;
  readonly runnerError: string;
  readonly dismiss: string;
  readonly actorLabel: Readonly<Record<'agent' | 'operator' | 'system', string>>;
  /** People directory. */
  readonly peopleTitle: string;
  readonly peopleIntro: string;
  readonly peopleEmpty: string;
  readonly peopleError: string;
  readonly retry: string;
  readonly saudiChip: string;
  /** Employee profile. */
  readonly personNotFound: string;
  readonly backToPeople: string;
  readonly profileEmployment: string;
  readonly profileIdentity: string;
  readonly profileSalary: string;
  readonly profileDocuments: string;
  readonly profileComputed: string;
  readonly fieldJobTitle: string;
  readonly fieldContractType: string;
  readonly fieldStartDate: string;
  readonly fieldEndDate: string;
  readonly fieldProbationEnd: string;
  readonly fieldNationalId: string;
  readonly fieldGosi: string;
  readonly fieldEmail: string;
  readonly salaryBasic: string;
  readonly salaryHousing: string;
  readonly salaryTransport: string;
  readonly salaryOther: string;
  readonly salaryTotal: string;
  readonly computedServiceYears: string;
  readonly computedLeaveDays: string;
  readonly computedEosb: string;
  readonly computedEosbNote: string;
  readonly docExpires: string;
  readonly contractIndefinite: string;
  readonly contractFixedTerm: string;
  /** Leave panel + request form (P5). */
  readonly profileLeave: string;
  readonly leaveEntitlement: string;
  readonly leaveTaken: string;
  readonly leaveRemaining: string;
  readonly leaveRequestTitle: string;
  readonly leaveStart: string;
  readonly leaveEnd: string;
  readonly leaveSubmit: string;
  /** Attention panels (wired to live cases from P5). */
  readonly attentionReview: string;
  readonly attentionExpiriesSoon: string;
  /** HR letters (P7). */
  readonly profileLetters: string;
  readonly letterSalary: string;
  readonly letterExperience: string;
}

const EN: Messages = {
  appTitle: 'SquadLab · People Engine',
  appWordmark: 'People Engine',
  badgeSimulation: 'Simulation',
  badgeSimulationLabel: 'Simulation mode: every adapter is a mock — no live AI, no real employee data.',
  loading: 'Loading…',
  errorGeneric: 'Something went wrong. Reload to try again.',
  empty: 'Nothing here.',
  attentionTitle: 'Attention',
  attentionIntro: 'Everything HR needs a human for today — agents handle the rest.',
  attentionGates: 'Approvals waiting',
  attentionExpiries: 'Documents expiring',
  attentionLeave: 'Leave requests',
  attentionEmpty: 'Nothing needs you yet — cases and reminders light up in the next increments (P4–P6).',
  navAttention: 'Attention',
  navPeople: 'People',
  navCases: 'Cases',
  navCommandCenter: 'Command Center',
  navCommandCenterLabel: 'Back to Command Center',
  ventureSwitcherLabel: 'Venture',
  casesTitle: 'Cases',
  casesIntro: 'Agent-run SOP executions — you only step in at the gates.',
  casesEmpty: 'No cases yet. Start one and watch the agent do the work.',
  casesError: 'Could not load cases. The case store did not respond.',
  startOnboarding: 'Start onboarding',
  startOnboardingFor: 'New joiner',
  caseNotFound: 'No such case in the active venture.',
  backToCases: 'All cases',
  caseStatusLabel: {
    open: 'Running',
    'awaiting-gate': 'Needs your approval',
    completed: 'Completed',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
  },
  gateApprove: 'Approve',
  gateRedo: 'Request redo',
  gateReject: 'Reject',
  gateHeading: 'Your call — the agent paused here',
  verdictAllowed: 'Within policy',
  verdictBlocked: 'Outside policy',
  runnerError: 'The case runner hit a problem',
  dismiss: 'Dismiss',
  actorLabel: { agent: 'Agent', operator: 'You', system: 'System' },
  peopleTitle: 'People',
  peopleIntro: 'Everyone in the active venture — profiles carry the Saudi paperwork that HR runs on.',
  peopleEmpty: 'No people in this venture yet.',
  peopleError: 'Could not load people. The registry did not respond.',
  retry: 'Retry',
  saudiChip: 'Saudi national',
  personNotFound: 'No such person in the active venture.',
  backToPeople: 'All people',
  profileEmployment: 'Employment',
  profileIdentity: 'Identity & government',
  profileSalary: 'Monthly salary',
  profileDocuments: 'Documents & expiries',
  profileComputed: 'Entitlements (computed)',
  fieldJobTitle: 'Job title',
  fieldContractType: 'Contract',
  fieldStartDate: 'Start date',
  fieldEndDate: 'End date',
  fieldProbationEnd: 'Probation ends',
  fieldNationalId: 'National ID / iqama',
  fieldGosi: 'GOSI number',
  fieldEmail: 'Email',
  salaryBasic: 'Basic',
  salaryHousing: 'Housing',
  salaryTransport: 'Transport',
  salaryOther: 'Other',
  salaryTotal: 'Total',
  computedServiceYears: 'Service',
  computedLeaveDays: 'Annual leave entitlement',
  computedEosb: 'Accrued end-of-service (indicative)',
  computedEosbNote: 'Full art. 84 award basis; the offboarding SOP applies the exit-specific fraction.',
  docExpires: 'expires',
  contractIndefinite: 'Indefinite',
  contractFixedTerm: 'Fixed-term',
  profileLeave: 'Leave balance',
  leaveEntitlement: 'Entitlement',
  leaveTaken: 'Taken',
  leaveRemaining: 'Remaining',
  leaveRequestTitle: 'Request leave',
  leaveStart: 'First day',
  leaveEnd: 'Last day',
  leaveSubmit: 'Send to the agent',
  attentionReview: 'Review',
  attentionExpiriesSoon: 'Nothing inside its renewal window.',
  profileLetters: 'Official letters',
  letterSalary: 'Salary certificate',
  letterExperience: 'Experience certificate',
};

const AR: Messages = {
  appTitle: 'سكواد لاب · محرك الموارد البشرية',
  appWordmark: 'محرك الموارد البشرية',
  badgeSimulation: 'محاكاة',
  badgeSimulationLabel: 'وضع المحاكاة: جميع الموصلات وهمية — لا ذكاء اصطناعي حي ولا بيانات موظفين حقيقية.',
  loading: 'جارٍ التحميل…',
  errorGeneric: 'حدث خطأ ما. أعد تحميل الصفحة للمحاولة مرة أخرى.',
  empty: 'لا يوجد شيء هنا.',
  attentionTitle: 'يتطلب انتباهك',
  attentionIntro: 'كل ما تحتاجه الموارد البشرية من إنسان اليوم — والوكلاء يتولّون الباقي.',
  attentionGates: 'موافقات بانتظارك',
  attentionExpiries: 'وثائق قاربت على الانتهاء',
  attentionLeave: 'طلبات إجازة',
  attentionEmpty: 'لا شيء يحتاجك الآن.',
  navAttention: 'الانتباه',
  navPeople: 'الموظفون',
  navCases: 'الحالات',
  navCommandCenter: 'مركز القيادة',
  navCommandCenterLabel: 'العودة إلى مركز القيادة',
  ventureSwitcherLabel: 'الشركة',
  casesTitle: 'الحالات',
  casesIntro: 'إجراءات تشغيلية ينفذها الوكلاء — تدخّلك فقط عند بوابات الاعتماد.',
  casesEmpty: 'لا حالات بعد. ابدأ واحدة وشاهد الوكيل ينجز العمل.',
  casesError: 'تعذّر تحميل الحالات. لم يستجب مخزن الحالات.',
  startOnboarding: 'بدء التعيين',
  startOnboardingFor: 'الموظف الجديد',
  caseNotFound: 'لا توجد هذه الحالة في الشركة النشطة.',
  backToCases: 'جميع الحالات',
  caseStatusLabel: {
    open: 'قيد التنفيذ',
    'awaiting-gate': 'بانتظار اعتمادك',
    completed: 'مكتملة',
    rejected: 'مرفوضة',
    cancelled: 'ملغاة',
  },
  gateApprove: 'اعتماد',
  gateRedo: 'طلب إعادة الصياغة',
  gateReject: 'رفض',
  gateHeading: 'القرار لك — توقف الوكيل هنا',
  verdictAllowed: 'ضمن السياسة',
  verdictBlocked: 'خارج السياسة',
  runnerError: 'واجه مشغّل الحالات مشكلة',
  dismiss: 'إغلاق',
  actorLabel: { agent: 'الوكيل', operator: 'أنت', system: 'النظام' },
  peopleTitle: 'الموظفون',
  peopleIntro: 'كل موظفي الشركة النشطة — الملفات تحمل الأوراق السعودية التي تُدار بها الموارد البشرية.',
  peopleEmpty: 'لا موظفين في هذه الشركة بعد.',
  peopleError: 'تعذّر تحميل الموظفين. لم يستجب السجل.',
  retry: 'إعادة المحاولة',
  saudiChip: 'سعودي الجنسية',
  personNotFound: 'لا يوجد هذا الشخص في الشركة النشطة.',
  backToPeople: 'جميع الموظفين',
  profileEmployment: 'التوظيف',
  profileIdentity: 'الهوية والجهات الحكومية',
  profileSalary: 'الراتب الشهري',
  profileDocuments: 'الوثائق وتواريخ الانتهاء',
  profileComputed: 'الاستحقاقات (محسوبة)',
  fieldJobTitle: 'المسمى الوظيفي',
  fieldContractType: 'العقد',
  fieldStartDate: 'تاريخ المباشرة',
  fieldEndDate: 'تاريخ الانتهاء',
  fieldProbationEnd: 'نهاية فترة التجربة',
  fieldNationalId: 'الهوية الوطنية / الإقامة',
  fieldGosi: 'رقم التأمينات الاجتماعية',
  fieldEmail: 'البريد الإلكتروني',
  salaryBasic: 'الأساسي',
  salaryHousing: 'بدل السكن',
  salaryTransport: 'بدل النقل',
  salaryOther: 'أخرى',
  salaryTotal: 'الإجمالي',
  computedServiceYears: 'مدة الخدمة',
  computedLeaveDays: 'استحقاق الإجازة السنوية',
  computedEosb: 'مكافأة نهاية الخدمة المتراكمة (استرشادية)',
  computedEosbNote: 'على أساس المادة ٨٤ كاملة؛ إجراء إنهاء الخدمة يطبّق النسبة حسب سبب انتهاء العلاقة.',
  docExpires: 'تنتهي في',
  contractIndefinite: 'غير محدد المدة',
  contractFixedTerm: 'محدد المدة',
  profileLeave: 'رصيد الإجازات',
  leaveEntitlement: 'الاستحقاق',
  leaveTaken: 'المستخدم',
  leaveRemaining: 'المتبقي',
  leaveRequestTitle: 'طلب إجازة',
  leaveStart: 'أول يوم',
  leaveEnd: 'آخر يوم',
  leaveSubmit: 'إرسال إلى الوكيل',
  attentionReview: 'مراجعة',
  attentionExpiriesSoon: 'لا شيء داخل نافذة التجديد.',
  profileLetters: 'الخطابات الرسمية',
  letterSalary: 'شهادة راتب',
  letterExperience: 'شهادة خبرة',
};

/** Resolve the message catalog for a locale (EN default; AR from P8). */
export function messagesFor(locale: string): Messages {
  return (locale.split('-')[0] ?? '').toLowerCase() === 'ar' ? AR : EN;
}
